import { useState, useMemo } from "react";
import { useAllSizeGrps } from "http-hooks/sizegrp";
import SetSizeGrp from "components/sizegrp/SetSizeGrp";
import DeleteSizeGrp from "components/sizegrp/DeleteSizeGrp";
import ManageSizeGroupDetails from "components/sizegrp/ManageSizeGroupDetails";
import { ActionButton } from "components/ui/Table";
import { CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { CogIcon } from "@heroicons/react/24/outline";
import InputSearch from "components/ui/InputSearch";
import Select from "components/ui/Select";
import Modal from "components/ui/modals";
import MasterPage, {
  FilterRow,
  FilterCell,
  ClearFiltersButton,
  AddButton,
  EditAction,
  DeleteAction,
} from "components/ui/MasterPage";

const typeOptions = [
  { value: "", label: "All Types" },
  { value: "SHP", label: "Shape" },
  { value: "CLR", label: "Color" },
  { value: "SZ", label: "Size" },
];

const rateConditionOptions = [
  { value: "", label: "All Conditions" },
  { value: "1", label: "With Rate" },
  { value: "0", label: "Without Rate" },
];

const statusOptions = [
  { value: "", label: "All Status" },
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
];

const initialFilter = {
  szgrpname: "",
  type: "",
  grpcond: "",
  ag_status: "",
  page: 1,
  limit: 10,
};

export default function OrderPrintGroupMaster() {
  const [filter, setFilter] = useState(initialFilter);
  const [isSetModalOpen, setIsSetModalOpen] = useState(false);
  const [editSizeGrpId, setEditSizeGrpId] = useState(null);
  const [deleteSizeGrpId, setDeleteSizeGrpId] = useState(null);
  const [manageSizeGroupId, setManageSizeGroupId] = useState(null);

  const {
    data: sizeGrpsData = {},
    isLoading: isAllSizeGrpsLoading,
    refetch: refetchSizeGrps,
  } = useAllSizeGrps(filter);

  const allSizeGrps = sizeGrpsData?.data || [];
  const totalPages = sizeGrpsData?.totalPages || 1;

  const handlePageChange = (newPage) => {
    setFilter((prev) => ({ ...prev, page: newPage }));
  };

  const handleEditClick = (szgrpid) => {
    setEditSizeGrpId(szgrpid);
    setIsSetModalOpen(true);
  };

  const handleCreateClick = () => {
    setEditSizeGrpId(null);
    setIsSetModalOpen(true);
  };

  const handleModalClose = () => {
    setIsSetModalOpen(false);
    setEditSizeGrpId(null);
  };

  const handleSetSuccess = () => {
    handleModalClose();
    refetchSizeGrps();
  };

  const handleManageSizesClick = (sizeGroup) => {
    setManageSizeGroupId(sizeGroup);
  };

  const handleManageSizesClose = () => {
    setManageSizeGroupId(null);
  };

  const getTypeLabel = useMemo(() => {
    const typeMap = {
      SHP: "Shape",
      CLR: "Color",
      SZ: "Size",
    };
    return (type) => typeMap[type] || type;
  }, []);

  const columns = useMemo(
    () => [
      { key: "szgrpname", header: "Group Name" },
      {
        key: "type",
        header: "Type",
        render: (sg) => getTypeLabel(sg.type),
      },
      { key: "colnos", header: "Columns" },
      {
        key: "grpcond",
        header: "Rate Condition",
        render: (sg) => (sg.grpcond ? "With Rate" : "Without Rate"),
      },
      {
        key: "ag_status",
        header: "Status",
        render: (sg) =>
          sg.ag_status === 1 ? (
            <CheckIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
          ) : (
            <XMarkIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
          ),
      },
      { key: "actions", isActions: true },
    ],
    [getTypeLabel]
  );

  return (
    <MasterPage
      title="Order Print Group Master"
      headerActions={
        <AddButton onClick={handleCreateClick}>Add Print Group</AddButton>
      }
      filters={
        <>
          <FilterRow>
            <FilterCell>
              <InputSearch
                onSearch={(searchValue) =>
                  setFilter((prev) => ({
                    ...prev,
                    szgrpname: searchValue,
                    page: 1,
                  }))
                }
                label="Group Name"
                placeholder="Search by name"
              />
            </FilterCell>
            <FilterCell>
              <Select
                options={typeOptions}
                value={typeOptions.find((o) => o.value === filter.type)}
                onChange={(selected) =>
                  setFilter((prev) => ({
                    ...prev,
                    type: selected?.value || "",
                    page: 1,
                  }))
                }
                label="Type"
                placeholder="Select type"
                isClearable={true}
              />
            </FilterCell>
            <FilterCell>
              <Select
                options={rateConditionOptions}
                value={rateConditionOptions.find(
                  (o) => o.value === filter.grpcond
                )}
                onChange={(selected) =>
                  setFilter((prev) => ({
                    ...prev,
                    grpcond: selected?.value || "",
                    page: 1,
                  }))
                }
                label="Rate Condition"
                placeholder="Select condition"
                isClearable={true}
              />
            </FilterCell>
            <FilterCell>
              <Select
                options={statusOptions}
                value={statusOptions.find(
                  (o) => o.value === filter.ag_status
                )}
                onChange={(selected) =>
                  setFilter((prev) => ({
                    ...prev,
                    ag_status: selected?.value || "",
                    page: 1,
                  }))
                }
                label="Status"
                placeholder="Select status"
                isClearable={true}
              />
            </FilterCell>
          </FilterRow>
          <FilterRow>
            <ClearFiltersButton onClick={() => setFilter(initialFilter)} />
          </FilterRow>
        </>
      }
      columns={columns}
      data={allSizeGrps}
      isLoading={isAllSizeGrpsLoading}
      rowKey="szgrpid"
      renderActions={(sizeGrp) => (
        <>
          <ActionButton
            onClick={() => handleManageSizesClick(sizeGrp)}
            variant="primary"
            title="Manage Sizes"
          >
            <CogIcon className="h-4 w-4" />
          </ActionButton>
          <EditAction
            onClick={() => handleEditClick(sizeGrp.szgrpid)}
            variant="secondary"
          />
          <DeleteAction
            onClick={() => setDeleteSizeGrpId(sizeGrp.szgrpid)}
          />
        </>
      )}
      extraModals={
        <>
          {isSetModalOpen && (
            <Modal open={isSetModalOpen} setOpen={handleModalClose}>
              <SetSizeGrp
                szgrpid={editSizeGrpId}
                onClose={handleModalClose}
                onSuccess={handleSetSuccess}
              />
            </Modal>
          )}
          {deleteSizeGrpId && (
            <Modal
              open={!!deleteSizeGrpId}
              setOpen={(val) => !val && setDeleteSizeGrpId(null)}
            >
              <DeleteSizeGrp
                id={deleteSizeGrpId}
                onClose={() => setDeleteSizeGrpId(null)}
              />
            </Modal>
          )}
          {manageSizeGroupId && (
            <Modal
              open={!!manageSizeGroupId}
              setOpen={(val) => !val && setManageSizeGroupId(null)}
              maxWidth="2xl"
            >
              <ManageSizeGroupDetails
                sizeGroup={manageSizeGroupId}
                onClose={handleManageSizesClose}
              />
            </Modal>
          )}
        </>
      }
      pagination={{
        currentPage: filter.page,
        totalPages,
        onPageChange: handlePageChange,
      }}
    />
  );
}
